import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import Utils from "../utils";
import { authActions } from "../store";
import configDb from "../config";
import { getSubscriptionList, SubscribeCompany } from "../services/AuthServices";
import { CreatePaymentStripe } from "../services/PaymentServices";

// Página dedicada quando a API devolve 402 (subscription expired /
// account blocked / unpaid). Permite ao master/owner renovar a
// subscrição inline (Stripe). Não navega para /dashboard ou outras
// páginas — todas as chamadas são whitelisted no backend para que
// não disparem 402.

const AccountBlocked = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [reason, setReason] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [yearly, setYearly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [stripeData, setStripeData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const u = Utils.loginDetail() || {};
    setUser(u);
    try {
      const wb = window.__tpAccountBlocked;
      if (wb && wb.message) {
        setReason(String(wb.message));
        return;
      }
    } catch (e) {}
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code === "SUBSCRIPTION_EXPIRED") setReason("A subscrição da empresa expirou.");
    else if (code === "SUBSCRIPTION_UNPAID") setReason("Existem pagamentos em atraso.");
    else if (code === "ACCOUNT_BLOCKED") setReason("A conta foi bloqueada.");
    else setReason("A conta da empresa não está activa.");
  }, []);

  // Master / owner = users.default_user=1 (definido em CompanyController
  // quando a empresa é criada). role_id===1 é super-admin TP (compat).
  const isMaster =
    !!user &&
    (Number(user.default_user) === 1 ||
      Number(user.role_id) === 1 ||
      user.is_owner === 1 ||
      user.is_owner === true);

  useEffect(() => {
    if (!isMaster) return;
    setLoadingPlans(true);
    getSubscriptionList()
      .then((r) => {
        const list = r?.data?.data || [];
        setPlans(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
      })
      .finally(() => setLoadingPlans(false));
  }, [isMaster]);

  // Stripe promise é criado quando recebemos a publish key do backend.
  useEffect(() => {
    if (stripeData?.stripe_publish_key) {
      setStripePromise(loadStripe(stripeData.stripe_publish_key));
    }
  }, [stripeData]);

  const handleLogout = () => {
    try {
      localStorage.removeItem(configDb?.data?.login_data);
      localStorage.removeItem("@secure.@data");
      dispatch(authActions.setAuth(null));
    } catch (e) {}
    navigate("/login", { replace: true });
  };

  const startStripe = (plan) => {
    const amount = yearly ? plan?.yearly_price : plan?.monthly_price;
    if (!amount || Number(amount) <= 0) {
      toast.error("Plano sem preço configurado.");
      return;
    }
    setSelectedPlan({ ...plan, _amount: amount });
    CreatePaymentStripe({ amount, currency: "eur" })
      .then((r) => setStripeData(r?.data?.data || null))
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setSelectedPlan(null);
      });
  };

  const handleSubscribed = () => {
    // Após pagamento bem-sucedido, marca subscrição activa server-side.
    SubscribeCompany({
      subscription_id: selectedPlan?.uuid || selectedPlan?.id,
      payment_method: "stripe",
      yearly: yearly ? 1 : 0,
    })
      .then(() => {
        toast.success("Subscrição activada. A redireccionar...");
        try { delete window.__tpAccountBlocked; } catch (e) {}
        setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const cancelStripe = () => {
    setSelectedPlan(null);
    setStripeData(null);
    setStripePromise(null);
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        background: "#f4f6f8",
        padding: 20,
      }}
    >
      <div
        style={{
          maxWidth: 720,
          width: "100%",
          background: "#fff",
          padding: 36,
          marginTop: 30,
          borderRadius: 12,
          boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "#fff5f5",
              color: "#dc3545",
              margin: "0 auto 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 36,
            }}
          >
            <i className="bi bi-shield-exclamation"></i>
          </div>
          <h2 style={{ marginBottom: 8, color: "#212529" }}>
            Conta sem subscrição activa
          </h2>
          <p style={{ color: "#495057", marginBottom: 4 }}>{reason}</p>
          <p style={{ color: "#6c757d", marginBottom: 0, fontSize: 14 }}>
            O acesso ficou bloqueado até a subscrição ser renovada.
          </p>
        </div>

        {!isMaster ? (
          <>
            <div className="alert alert-warning">
              <i className="bi bi-info-circle-fill me-2"></i>
              Apenas o administrador da conta principal pode renovar a
              subscrição. Por favor contacta o responsável da tua empresa.
            </div>
            <div className="text-center">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-2"></i>
                Terminar sessão
              </button>
            </div>
          </>
        ) : selectedPlan && stripeData && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret: stripeData.client_secret }}>
            <StripeForm
              plan={selectedPlan}
              yearly={yearly}
              onSuccess={handleSubscribed}
              onCancel={cancelStripe}
            />
          </Elements>
        ) : (
          <>
            <div
              className="d-flex justify-content-center align-items-center mb-3"
              style={{ gap: 10 }}
            >
              <span style={{ fontWeight: !yearly ? 700 : 400 }}>Mensal</span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={yearly}
                  onChange={() => setYearly((v) => !v)}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <span style={{ fontWeight: yearly ? 700 : 400 }}>
                Anual <small className="text-success">(2 meses grátis)</small>
              </span>
            </div>

            {loadingPlans ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">A carregar...</span>
                </div>
              </div>
            ) : plans.length === 0 ? (
              <div className="alert alert-info">Sem planos disponíveis.</div>
            ) : (
              <div className="row g-3">
                {plans.map((p) => {
                  const price = yearly ? p.yearly_price : p.monthly_price;
                  return (
                    <div className="col-md-6" key={p.id}>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: 8,
                          padding: 16,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <h5 style={{ marginBottom: 6 }}>{p.name}</h5>
                        {p.description && (
                          <div className="text-muted small mb-2">
                            {p.description}
                          </div>
                        )}
                        <div style={{ flex: 1 }}></div>
                        <div style={{ marginTop: 8 }}>
                          <div
                            style={{
                              fontSize: 22,
                              fontWeight: 700,
                              color: "#0d6efd",
                            }}
                          >
                            {Number(price).toFixed(2)} €
                            <small
                              style={{ fontSize: 12, color: "#6c757d", fontWeight: 400 }}
                            >
                              {" "}
                              / {yearly ? "ano" : "mês"}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary w-100 mt-2"
                            onClick={() => startStripe(p)}
                            disabled={!price || Number(price) <= 0}
                          >
                            <i className="bi bi-credit-card-2-front me-1"></i>
                            Subscrever
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="text-center mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-2"></i>
                Terminar sessão
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Stripe form (inline) ────────────────────────────────────
const StripeForm = ({ plan, yearly, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const result = await stripe.confirmCardPayment(
        elements._commonOptions?.clientSecret ||
          (await elements._commonOptions?.clientSecretPromise) ||
          undefined,
        {
          payment_method: { card: elements.getElement(CardElement) },
        }
      );
      if (result.error) {
        toast.error(result.error.message || "Erro no pagamento.");
        setSubmitting(false);
        return;
      }
      if (result.paymentIntent?.status === "succeeded") {
        onSuccess();
      } else {
        toast.error("Pagamento não concluído.");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.message || "Erro inesperado.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div
        style={{
          background: "#f8f9fa",
          padding: 14,
          borderRadius: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <strong>{plan?.name}</strong>
          <strong>
            {Number(plan?._amount).toFixed(2)} €
            <small className="text-muted">
              {" "}
              / {yearly ? "ano" : "mês"}
            </small>
          </strong>
        </div>
      </div>
      <div
        style={{
          border: "1px solid #ced4da",
          borderRadius: 6,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <CardElement options={{ hidePostalCode: true }} />
      </div>
      <div className="d-flex" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-outline-secondary flex-fill"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="btn btn-primary flex-fill"
          disabled={submitting || !stripe}
        >
          {submitting ? (
            <span className="spinner-border spinner-border-sm me-2"></span>
          ) : (
            <i className="bi bi-credit-card-2-front me-2"></i>
          )}
          Pagar e activar
        </button>
      </div>
    </form>
  );
};

export default AccountBlocked;
